import { useTranslation } from "react-i18next";
import { AppToolbar, CURRENT_YEAR_MAX_DATE, DatePickerComponent, DatePickerWrapper, MIN_DATE } from "../components/AppToolbar";
import WorkLogReport from "../models/WorkLogReport";
import { Button, TableCell, Tooltip } from "@mui/material";
import ReportTable from "../components/ReportTable";
import { useLocation } from "react-router-dom";
import { RequestStatus } from "../types/RequestStatus";
import OpsDateUtils from "../utils/OpsDateUtils";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import { SpinnerWrapper } from "../components/SpinnerWrapper";
import { WorkLogWeeklyValidationCheckboxComponent } from "../components/WorklogReport/WorkLogWeeklyValidationCheckbox";
import useWorklogReportList, { WORKDAYS_HEADER_TYPE } from "../hooks/useWorklogReportList";
import WorklogReportDownload from "../components/WorklogReport/WorklogReportDownload";
import DialogHoursAdjustment from "../components/WorklogReport/DialogHoursAdjustment";
import React, { useRef, useState, MouseEvent, useEffect, useMemo } from "react";
import { Divider, Menu, MenuItem, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import FilterDialog, { FilterDialogButton } from "../components/FilterDialog";
import useWindowSize from "../hooks/useWindowSize";
import { SimpleAutoComplete } from "../components/SimpleAutoComplete";
import { CheckboxComponent } from "../components/CheckboxComponent";
import { MOBILE_WIDTH } from "../App";
import { ReportHeader } from "../external/ensolvers-core-frontend-mui/dto/ReportDTO";
import useTableHeight from "../hooks/useTableHeight";
import WorkLogPopup from "../components/WorklogReport/WorkLogPopup";
import useUpdateUrl from "../hooks/useUpdateUrl";
import { Routes } from "../types/Routes";
import RoleUtils from "../utils/RoleUtils";
import WorkLogMonthlyService from "../services/WorkLogMonthlyService";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import { AppDialog } from "../components/AppDialog";
import BatchHoursAdjustmentButton from "../components/Worklog/BatchHoursAdjustmentButton";
import { ButtonComponent } from "../components/ButtonComponent";
import { BatchApprovalErrorDTO } from "../models/BatchApprovalErrorDTO";
import { ReportTableRow } from "../components/WorklogReport/ReportTableRow";
import { SelectAllCheckbox } from "../components/WorklogReport/SelectAllCheckbox";
import { unstable_batchedUpdates } from "react-dom";
import { EditDialog } from "../external/ensolvers-core-frontend-mui/components/EditDialog";
import Assignation from "../models/Assignation";
import { DialogPosition } from "../external/ensolvers-core-frontend-mui/components/Common/DialogPosition";
import AssignationEditionFields from "../components/EditionDialogs/Fields/AssignationEditionFields";
import { useStyles as formStyles } from "../components/Main/formStyles";
import useAssignationList from "../hooks/useAssignationList";
import AssignationService from "../services/AssignationService";
import Position from "../models/Position";
import WorklogReportUtils from "../utils/WorklogReportUtils";
import PositionFormFields from "../components/EditionDialogs/Fields/PositionFormFields";
import WorkLogService from "../services/WorkLogService";
import { BillingPolicy, SUM_HOURS_WITH_MINIMUM_KEY } from "../types/BillingPolicy";
import PositionService from "../services/PositionService";
import ApiErrorUtils from "../utils/ApiErrorUtils";
import { BooleanSegmented } from "../components/SegmentedControl";
import { filtersBarStyle, Token } from "../theme";

export default function WorkLogReportList() {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { isMobileWidth } = useWindowSize();
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLButtonElement | null>(null);
  const [day, setDay] = useState<number | null>(null);
  const [locked, setLocked] = useState<boolean>(false);

  const { t } = useTranslation();
  const projectNameInputRef = useRef<HTMLSelectElement>();

  const [confirmationOpen, setConfirmationOpen] = useState<boolean>(false);
  const [selectedWorkLogs, setSelectedWorkLogs] = useState<WorkLogReport[]>([]);
  const [processing, setProcessing] = useState<boolean>(false);
  const [selectedEditAssignation, setSelectedEditAssignation] = useState<Assignation | undefined>(undefined);
  const [selectedEditPosition, setSelectedEditPosition] = useState<Position | undefined>(undefined);
  const [isEditingAssignation, setIsEditingAssignation] = useState<boolean>(false);
  const [isEditingPosition, setIsEditingPosition] = useState<boolean>(false);
  const [hasWorklogs, setHasWorklogs] = useState<boolean>(false);
  const [policyWithMinimum, setPolicyWithMinimum] = useState<boolean>(false);
  const [positionBonus, setPositionBonus] = useState(0);
  const [isShadowChecked, setIsShadowChecked] = useState(false);
  const [batchErrors, setBatchErrors] = useState<BatchApprovalErrorDTO[]>([]);
  const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);
  const customStyles = formStyles();

  const {
    worklogReportState,
    requestStatus,
    handleChangeExternalProjects,
    headers,
    rows,
    setRows,
    selectedDate,
    setSelectedDate,
    isLoading,
    setIsLoading,
    canEdit,
    search,
    setSearch,
    projectNames,
    setStoredProject,
    onlyExternal,
    assigned,
    handleOnChangeWorklogReport,
    workdays,
    findMinDayOfCurrentWeek,
    selectedReport,
    setSelectedReport,
    onWorkLogUpdate,
  } = useWorklogReportList();

  useEffect(() => {
    async function fetchData() {
      const response = await WorkLogMonthlyService.getByDateAndProject(
        search.externalId,
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
      );
      const workLogMonthly = response.getContentOrThrowError();
      setLocked(workLogMonthly.locked);
    }
    if (search.externalId !== "") {
      fetchData();
    }
  }, [search, selectedDate]);

  const searchParams = new URLSearchParams(useLocation().search);
  const { updateUrl } = useUpdateUrl(Routes.WORKLOG_REPORT, searchParams);

  const onApproveMonthClicked = async () => {
    NotificationService.info("Week batch sent to process");
    setIsLoading(true);
    let reportList = selectedWorkLogs;

    const assignationIdList: string[] = reportList.map((weeklyWorklogs, index) => {
      return weeklyWorklogs.assignationExternalId;
    });

    const dateList: Date[] = Object.entries(reportList[0].hours as Map<number, number>).map(([week, value], i) => {
      let shortWeekFirstDay = OpsDateUtils.getFirstDayOfWeekInMonth(selectedDate, parseInt(week) - 1);
      return OpsDateUtils.toUTC(shortWeekFirstDay);
    });

    try {
      const batchResponse = await worklogReportState.approveWeeks({
        assignationExternalIdList: assignationIdList,
        shortWeekFirstDayList: dateList,
        validated: true,
      });
      if (batchResponse.elementWithErrors > 0) {
        setBatchErrors(batchResponse.errors || []);
        setShowErrorDialog(true);
        NotificationService.warn(
          `There were ${batchResponse.totalErrors} unapproved weeks across ${batchResponse.elementWithErrors} workers. Click the dialog for details.`,
        );
      } else {
        NotificationService.info("All weeks were successfully approved");
      }
      setSelectedWorkLogs([]);
    } catch {
      // QueryClient already toasts ApiError from the failed approve.
    } finally {
      setIsLoading(false);
    }
  };

  const checkProjectSelected = (): boolean => {
    // Check if the externalId is empty or null
    if (search.externalId === "") {
      NotificationService.error("Please select a project");
      return false;
    }
    return true;
  };

  const handleOpenPopover = (report: WorkLogReport, day: number, event: MouseEvent<HTMLButtonElement>): void => {
    if (popoverAnchor) {
      handleClosePopover();
    }
    setDay(day);
    setSelectedReport(report);
    setPopoverAnchor(event.currentTarget);
  };

  const handleClosePopover = (): void => {
    setSelectedReport(null);
    setPopoverAnchor(null);
  };

  const handleLockReport = (): void => {
    if (locked && !RoleUtils.isAdmin()) {
      NotificationService.error("Only admins can open a report");
      setLocked(true);
      return;
    }
    if (!checkProjectSelected()) return;
    setConfirmationOpen(true);
  };
  const confirmAction = () => {
    async function fetchData() {
      const response = await WorkLogMonthlyService.lockReport(search.externalId, selectedDate, locked);

      if (!response.isSuccess()) {
        NotificationService.error("Cannot close this report. Check if all weeks had been validated.");
        return;
      }
      setLocked(response.getContentOrThrowError().locked);
    }
    fetchData();
    setConfirmationOpen(false);
  };

  const renderProjectNameAutoComplete = (
    <SimpleAutoComplete
      ref={projectNameInputRef}
      label="Project name"
      items={projectNames}
      value={search}
      onChange={(newValue) => {
        setSearch(newValue);
        setStoredProject(newValue);
        setIsLoading(!isLoading);
        searchParams.set("project", newValue.externalId);
        setSelectedWorkLogs([]);
        updateUrl();
      }}
    />
  );
  const renderFilters = () => {
    return isMobileWidth() ? (
      <div className={classes.mobileFilters}>
        {renderProjectNameAutoComplete}
        <FilterDialogButton isOpen={isOpen} text="Filters & Downloads" setIsOpen={setIsOpen} />
      </div>
    ) : (
      <div className={classes.desktopFilters}>
        <div className={classes.filtersGroup}>
          {renderProjectNameAutoComplete}
          {renderDialogFilters}
        </div>
      </div>
    );
  };

  const renderReportActions = () =>
    isMobileWidth() ? undefined : (
      <div className={classes.reportActions}>
        <WorklogReportDownload
          className={classes.downloadRow}
          date={selectedDate}
          search={search}
          focusProjectNameInput={() => {
            projectNameInputRef.current?.click();
          }}
        />
        {renderCloseButton()}
      </div>
    );

  const renderDialogFilters = (
    <>
      <DatePickerWrapper
        className="date-picker"
        style={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", columnGap: "10px" }}
      >
        <DatePickerComponent
          views="month"
          label="Month"
          value={selectedDate}
          inputFormat="MMMM"
          onChange={(value) => {
            setSelectedDate(value!);
            setIsLoading(true);
            searchParams.set("month", value!.getMonth().toString());
            updateUrl();
          }}
        />
        <DatePickerComponent
          views="year"
          label="Year"
          value={selectedDate}
          inputFormat="yyyy"
          minDate={MIN_DATE}
          maxDate={CURRENT_YEAR_MAX_DATE}
          onChange={(selectedDate) => {
            setSelectedDate(selectedDate!);
            setIsLoading(true);
            searchParams.set("year", selectedDate!.getFullYear().toString());
            updateUrl();
          }}
        />
      </DatePickerWrapper>
      <div className="check-boxes" style={{ display: "inline-block", width: "max-content" }}>
        <BooleanSegmented
          checked={onlyExternal}
          onToggle={handleChangeExternalProjects}
          offLabel="All"
          onLabel="External"
          label="Projects"
          ariaLabel="External projects only"
        />
      </div>
      <div className="check-boxes" style={{ display: "inline-block", width: "max-content" }}>
        <BooleanSegmented
          checked={assigned}
          onToggle={handleOnChangeWorklogReport}
          offLabel="All"
          onLabel="Mine"
          label="Assigned"
          ariaLabel="Assigned to me"
        />
      </div>
      <div>
        <BatchHoursAdjustmentButton worklogReportState={worklogReportState} worklogs={selectedWorkLogs} processing={processing} />
      </div>
      <div>
        <ButtonComponent
          disabled={selectedWorkLogs.length < 1}
          debounce={true}
          onClick={onApproveMonthClicked}
          text={"Approve Entire Month for Selected Members"}
          style={{
            margin: "0% ",
            paddingRight: "1.125rem",
            height: "100%",
            padding: "0.75rem",
            borderRadius: "1.375rem",
            minWidth: "5rem",
          }}
        />
      </div>

      {isMobileWidth() && (
        <>
          <Divider style={{ marginBottom: ".7rem" }} />
          <WorklogReportDownload
            className={classes.downloadSection}
            date={selectedDate}
            search={search}
            focusProjectNameInput={() => {
              projectNameInputRef.current?.click();
            }}
          />
        </>
      )}
    </>
  );
  const renderCloseButton = () => (
    <>
      {RoleUtils.isAdminOrManager() && (
        <>
          <ButtonComponent
            text={locked ? "open report" : "close report"}
            onClick={handleLockReport}
            sx={{
              color: Token.textOnAction,
              backgroundColor: Token.actionPrimary,
              "&:hover": {
                backgroundColor: Token.actionPrimaryHover,
              },
            }}
          />
          <AppDialog
            open={confirmationOpen}
            onClose={() => setConfirmationOpen(false)}
            title={locked ? "Open Report" : "Close Report"}
            content={
              <Typography>
                {`You are about to ${locked ? "open" : "close"} the report. ${locked ? "" : "This action can be only undone by admins."} Are you should you want to proceed?`}
              </Typography>
            }
            buttons={[{ label: "Confirm", onClick: confirmAction }]}
          />
        </>
      )}
    </>
  );

  const renderCell = (instance: WorkLogReport, week: string, value: number, index: number) => {
    const day = OpsDateUtils.getFirstDayOfWeekInMonth(selectedDate || new Date(), parseInt(week) - 1).getDate();

    let bonused: boolean = false;
    let inDebt: boolean = false;
    let isAssignmentFinished: boolean = false;
    let isInDebtAndWeeklyIsNotApproved: boolean = false;
    let hasNotStarted: boolean = false;
    let fullyValidated: boolean = false;
    if (headers && instance.memberFullName !== "Total hours" && workdays && workdays[index] && workdays[index].workdays) {
      let workdaysObject = workdays![index].workdays;
      const userCountry = instance.country;
      const countryWorkdays = workdaysObject.find((wd) => userCountry === wd.country)!;
      const cellDate = OpsDateUtils.addWeeksToDate(OpsDateUtils.getfirstDayOfTheYear(selectedDate.getFullYear()), parseInt(week));
      const currentWeekMaxDate = findMinDayOfCurrentWeek();
      bonused = value > countryWorkdays?.count * instance.externalHours!;
      inDebt = value < countryWorkdays?.count * instance.externalHours!;
      isAssignmentFinished =
        (instance.assignationEndDate &&
          OpsDateUtils.isSameOrBefore(
            instance.assignationEndDate,
            OpsDateUtils.addWeeksToDate(OpsDateUtils.getfirstDayOfTheYear(selectedDate.getFullYear()), parseInt(week)),
          )) ||
        false;

      if (inDebt) {
        isInDebtAndWeeklyIsNotApproved = (instance.validated && instance.validated[week] === false) || false;
      }

      fullyValidated = !inDebt && instance.validated && instance.validated[week] === true;

      hasNotStarted = currentWeekMaxDate !== null && OpsDateUtils.isAfter(cellDate, currentWeekMaxDate);
    }
    let backgroundColor = {};
    let checkboxColor = { color: Token.textTertiary };

    if (!hasNotStarted && !instance.assignationPositionOnDemand) {
      if (isAssignmentFinished) {
        backgroundColor = { backgroundColor: Token.borderDefault };
      } else if (isInDebtAndWeeklyIsNotApproved) {
        backgroundColor = { backgroundColor: Token.statusDangerBg };
        checkboxColor = { color: Token.statusDangerFg };
      } else if (inDebt) {
        backgroundColor = { backgroundColor: Token.statusWarningBg };
        checkboxColor = { color: Token.statusWarningFg };
      } else if (fullyValidated) {
        backgroundColor = { backgroundColor: Token.statusSuccessBg };
        checkboxColor = { color: Token.statusInfoFg };
      } else if (bonused) {
        backgroundColor = { backgroundColor: Token.statusInfoBg };
        checkboxColor = { color: Token.statusInfoFg };
      }
    }

    return (
      <TableCell
        sx={{
          "& div": {
            backgroundColor: backgroundColor,
            ".MuiSvgIcon-root": {
              color: checkboxColor,
            },
          },
        }}
        key={`${instance.memberFullName}-${index}`}
      >
        <div className={classes.tableCell}>
          {instance.memberFullName === "Total hours" || instance.memberFullName === "workdays" || instance.memberFullName === "holidays" ? (
            value
          ) : (
            <>
              <Button className={classes.linkButton} onClick={(event) => handleOpenPopover(instance, day, event)}>
                {instance.adjustedHoursPerWeek[week] !== 0 ? (
                  <>
                    {value} ({instance.adjustedHoursPerWeek[week] > 0 && "+"}
                    {instance.adjustedHoursPerWeek[week]})
                  </>
                ) : (
                  value
                )}
              </Button>

              <WorkLogWeeklyValidationCheckboxComponent
                instance={instance}
                date={selectedDate || OpsDateUtils.getCurrentDay()}
                week={week}
                rows={rows}
                setRows={setRows}
                canEdit={canEdit}
              />
            </>
          )}
        </div>
      </TableCell>
    );
  };

  const createTextForToolTip = (header: ReportHeader) => {
    return (
      <p>
        {workdays &&
          workdays[header.index!].workdays.map((w, index) => (
            <React.Fragment key={`wh-${index}`}>
              {w.country}: {w.count}
              <br />
            </React.Fragment>
          ))}
      </p>
    );
  };

  const renderCustomCell = (header: ReportHeader, index: number) => {
    return header.type === WORKDAYS_HEADER_TYPE ? (
      <TableCell key={`h-${index}`} align={header.align} width={header.width}>
        <Tooltip placement="top" title={createTextForToolTip(header)}>
          <span> {header.label}</span>
        </Tooltip>
      </TableCell>
    ) : (
      <TableCell key={`h-${index}`} align={header.align} width={header.width}>
        {header.label}
      </TableCell>
    );
  };

  const renderSelectAllTile = (header: ReportHeader) => {
    return (
      <TableCell
        key={`main-check`}
        align={"left"}
        width={250}
        style={{
          fontWeight: "bold",
          paddingLeft: "1rem",
        }}
      >
        <SelectAllCheckbox
          allSelected={allSelected}
          isIndeterminate={isIndeterminate}
          onChange={handleSelectAll}
          style={{ marginRight: "20%" }}
        />
        {header.label}
      </TableCell>
    );
  };

  async function handleMenuClick(event: MouseEvent<HTMLButtonElement>, instance: WorkLogReport) {
    if (menuAnchor !== event.currentTarget) {
      setMenuAnchor(event.currentTarget);
      let assignationResponse = await AssignationService.getByExternalId(instance.assignationExternalId);
      const assignation = assignationResponse.getContentOrThrowError();
      let positionResponse = await PositionService.findPositionByExternalId(assignation.position.externalId);

      setSelectedEditAssignation(assignation);
      const position = positionResponse.getContentOrThrowError();
      setSelectedEditPosition(position);
      checkWorklogs(position);
    }
  }

  function handleMenuClose() {
    setMenuAnchor(null);
  }

  const navigateTo = (section: "positions" | "assignations") => {
    searchParams.set("project", search.externalId);
    updateUrl(section, searchParams, true);
  };

  const processBatch = () => {
    setProcessing(true);
    worklogReportState.handleBatchAdjustHours().then((r) => setProcessing(false));
  };

  const selectedWorkLogsSet = useMemo(() => new Set(selectedWorkLogs.map((log) => log.index)), [selectedWorkLogs]);

  // Include only rows that represent actual workers
  const selectableRows = useMemo(
    () =>
      rows.filter(
        (row) => row.userExternalId && row.assignationExternalId, // Properties that indicate the row represents an actual worker
      ),
    [rows],
  );

  const allSelected = useMemo(
    () => selectableRows.length > 0 && selectableRows.every((row) => selectedWorkLogsSet.has(row.index)),
    [selectableRows, selectedWorkLogsSet],
  );

  const isIndeterminate = useMemo(
    () => selectableRows.some((row) => selectedWorkLogsSet.has(row.index)) && !allSelected,
    [selectableRows, selectedWorkLogsSet, allSelected],
  );

  const handleSelectAll = (checked: boolean) => {
    unstable_batchedUpdates(() => {
      setSelectedWorkLogs((prev) => {
        if (checked) {
          // When selecting all, include only rows that represent actual workers
          const selectableRows = rows.filter((row) => row.userExternalId && row.assignationExternalId);
          const newSelections = selectableRows.filter((row) => !prev.some((log) => log.index === row.index));
          return [...prev, ...newSelections];
        } else {
          return prev.filter((log) => !rows.some((row) => row.index === log.index));
        }
      });
    });
  };

  const handleOnUpdateAssignation = async (instance: Assignation) => {
    if (!canEdit) {
      return undefined;
    }

    setIsLoading(true);

    await AssignationService.update(instance)
      .then((response) => response.getContentOrThrowError())
      .then(() => {
        NotificationService.info("Assignation updated successfully");
      })
      .catch((error) => {
        NotificationService.error(ApiErrorUtils.getMessage(error, "Error updating assignation"));
      })
      .finally(() => {
        worklogReportState.setAssignationUpdated(instance);
        setIsLoading(false);
      });
  };

  function handleOnCancelEditModals(): void {
    setIsEditingAssignation(false);
    setIsEditingPosition(false);
    setSelectedEditAssignation(undefined);
    setSelectedEditPosition(undefined);
    setHasWorklogs(false);
  }

  const checkWorklogs = async (instance: Position) => {
    let worklogs = await WorkLogService.findByPositionExternalId(instance.externalId!);
    setHasWorklogs(worklogs.getContentOrThrowError().length > 0);
  };

  const handleOnChangeInputBillingPolicy = (event: any) => {
    const value = event.target.value;
    if (value === SUM_HOURS_WITH_MINIMUM_KEY) {
      setPolicyWithMinimum(true);
    } else {
      setPolicyWithMinimum(false);
    }
    setPositionBonus(0);
  };

  const handleOnCheckShadow = (event: any) => {
    setIsShadowChecked(!isShadowChecked);
    setPositionBonus(0);
  };

  const handleOnChangeBonus = (event: any) => {
    const newValue = parseInt(event.target.value);
    setPositionBonus(newValue);
    setIsShadowChecked(false);
  };

  const onUpdatePosition = async (position: Position) => {
    setIsLoading(true);
    try {
      let worklogs = await WorkLogService.findByPositionExternalId(position.externalId!);
      setHasWorklogs(worklogs.getContentOrThrowError().length > 0);
      setPolicyWithMinimum(position.billingPolicy === BillingPolicy.SUM_HOURS_WITH_MINIMUM);
      (await PositionService.update(position)).getContentOrThrowError();
      NotificationService.info("Position updated successfully!");
    } catch (err: any) {
      NotificationService.error(ApiErrorUtils.getMessage(err, "Error updating position"));
    } finally {
      worklogReportState.setPositionUpdated(position);
      setIsLoading(false);
    }
  };

  const handlePermissionError = () => {
    setIsLoading(true);
    NotificationService.error("You don't have permission to do this action");
    setIsLoading(false);
  };

  return requestStatus === RequestStatus.ERROR ? (
    <></>
  ) : (
    <>
      <AppContainerFullHeight
        bottomSectionRef={bottomSectionRef}
        topSection={
          <AppToolbar title={t("Worklog report")} rightContent={renderReportActions()}>
            {renderFilters()}
          </AppToolbar>
        }
        bottomSection={
          <>
            {!!search && search.externalId !== "" && (
              <SpinnerWrapper isLoading={isLoading}>
                <ReportTable<WorkLogReport>
                  maxHeight={calculateMaxHeight(false)}
                  stickyHeader
                  renderCustomCell={renderCustomCell}
                  renderSelectAllHeader={renderSelectAllTile}
                  headers={headers!}
                  rows={rows}
                  denseTable
                  tableRow={(instance) => (
                    <>
                      <ReportTableRow
                        instance={instance}
                        isSelected={!!selectedWorkLogs.find((e) => e.index === instance.index)}
                        onToggleSelect={(index) => {
                          setSelectedWorkLogs((prevSelected) => {
                            const foundIndex = prevSelected.findIndex((e) => e.index === index);
                            if (foundIndex === -1) {
                              return [...prevSelected, instance];
                            } else {
                              return prevSelected.filter((e) => e.index !== index);
                            }
                          });
                        }}
                        handleMenuClick={handleMenuClick}
                        menuAnchor={menuAnchor}
                        selectedDate={selectedDate}
                        canEdit={canEdit}
                        classes={classes}
                        renderCell={renderCell}
                        worklogReportState={worklogReportState}
                      />
                    </>
                  )}
                />
              </SpinnerWrapper>
            )}
            <DialogHoursAdjustment
              worklogReport={worklogReportState.reportToAdjust || (processing ? undefined : worklogReportState.batchReportsToAdjust?.[0])}
              clearWorklogReport={() => {
                worklogReportState.clearWorklogReport();
                worklogReportState.clearBatchReport();
              }}
              handleAdjustHours={worklogReportState.reportToAdjust ? worklogReportState.handleAdjustHours : processBatch}
            />
          </>
        }
      />

      {/* Menu to navigate to positions/assignations tabs */}
      <Menu
        id="simple-menu"
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        MenuListProps={{ onMouseLeave: handleMenuClose }}
      >
        <MenuItem
          onClick={() => {
            setIsEditingAssignation(true);
          }}
        >
          Edit Assignation
        </MenuItem>
        <MenuItem
          onClick={() => {
            setIsEditingPosition(true);
          }}
        >
          Edit Position
        </MenuItem>
      </Menu>

      {/* Dialog to manage filters  */}
      <FilterDialog title={"Worklogs"} useCustomStyle={true} setIsOpen={setIsOpen} isOpen={isOpen}>
        <div className={classes.dialogFilters}>{renderDialogFilters}</div>
      </FilterDialog>

      {selectedReport && (
        <WorkLogPopup
          report={selectedReport}
          date={selectedDate}
          day={day!}
          anchorEl={popoverAnchor}
          onClose={handleClosePopover}
          onWorkLogUpdate={onWorkLogUpdate}
        ></WorkLogPopup>
      )}

      <EditDialog<Assignation>
        headerStyles={{ color: Token.textPrimary, marginBottom: -10, marginTop: 10 }}
        buttonStyles={{ marginRight: 20, color: "white", backgroundColor: Token.actionPrimary, marginBottom: 10 }}
        cancelButtonStyles={{
          color: Token.textSecondary,
          border: `1px solid ${Token.borderDefault}`,
          marginBottom: 10,
        }}
        dialogStyles={customStyles.root}
        elementName={"Assignation"}
        dialogPosition={DialogPosition.Center}
        currentInstance={selectedEditAssignation}
        newItemDialogOpen={isEditingAssignation}
        onCreate={() => {}}
        onUpdate={canEdit ? handleOnUpdateAssignation : handlePermissionError}
        onCancel={handleOnCancelEditModals}
        renderDialogContent={(register, errors, control) => (
          <>
            <AssignationEditionFields
              control={control}
              register={register}
              errors={errors}
              onStartDateChange={(date) => {}}
              onEndDateChange={(date) => {}}
            />
          </>
        )}
        setNewInstanceDialogOpen={() => {}}
        setLastUpdateTimestamp={() => {}}
      />
      <EditDialog<Position>
        headerStyles={{ color: Token.textPrimary, marginBottom: -10, marginTop: 10 }}
        buttonStyles={{ marginRight: 20, color: "white", backgroundColor: Token.actionPrimary, marginBottom: 10 }}
        cancelButtonStyles={{
          color: Token.textSecondary,
          border: `1px solid ${Token.borderDefault}`,
          marginBottom: 10,
        }}
        dialogStyles={customStyles.root}
        elementName={"Position"}
        dialogPosition={DialogPosition.Center}
        currentInstance={selectedEditPosition}
        newItemDialogOpen={isEditingPosition}
        onCreate={() => {}}
        onUpdate={canEdit ? onUpdatePosition : handlePermissionError}
        onCancel={handleOnCancelEditModals}
        renderDialogContent={(register, errors, control) => (
          <PositionFormFields
            register={register}
            errors={errors}
            control={control}
            newBillingPolicy={policyWithMinimum}
            onChangeBillingPolicy={handleOnChangeInputBillingPolicy}
            disabledBonusHours={isShadowChecked}
            onChangeBonus={handleOnChangeBonus}
            readonlyShadowCheckbox={selectedEditPosition ? hasWorklogs || positionBonus > 0 : false}
            onCheckShadow={handleOnCheckShadow}
          />
        )}
        setNewInstanceDialogOpen={() => {}}
        setLastUpdateTimestamp={() => {}}
      />

      {/* Dialog to show batch approval errors */}
      <AppDialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Batch Approval Errors"
        content={
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            {batchErrors.length > 0 ? (
              <>
                <Typography variant="body1" style={{ marginBottom: "1rem", fontWeight: "bold" }}>
                  The following errors occurred during batch approval:
                </Typography>
                {batchErrors.map((error, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: "1rem",
                      padding: "0.75rem",
                      border: `1px solid ${Token.borderDefault}`,
                      borderRadius: "4px",
                      backgroundColor: Token.surfaceSunken,
                    }}
                  >
                    <Typography variant="body2" style={{ fontWeight: "bold", marginBottom: "0.25rem" }}>
                      Worker: {error.userFullName}
                    </Typography>
                    <Typography variant="body2" style={{ marginBottom: "0.25rem" }}>
                      Position: {error.positionName}
                    </Typography>
                    <Typography variant="body2" style={{ marginBottom: "0.25rem" }}>
                      Week: {OpsDateUtils.formatInUTCAsDDDMMMYYY(new Date(error.shortWeekFirstDay))}
                    </Typography>
                    <Typography variant="body2" style={{ color: Token.statusDangerFg, fontWeight: "bold" }}>
                      Error: {error.errorMessage}
                    </Typography>
                  </div>
                ))}
              </>
            ) : (
              <Typography>No errors to display</Typography>
            )}
          </div>
        }
        buttons={[{ label: "Close", onClick: () => setShowErrorDialog(false) }]}
      />
    </>
  );
}

const useStyles = makeStyles(
  (theme) => ({
    // The bar itself never wraps: the filters wrap inside their own group and the downloads stay
    // pinned to the right, the way the panel in Me > Work Logs behaves.
    desktopFilters: {
      ...filtersBarStyle(),
      flexWrap: "nowrap",
      alignItems: "flex-start",
      gap: "16px",
      position: "relative",
    },
    filtersGroup: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: "10px",
      flex: "1 1 auto",
      minWidth: 0,
      "& > div ": {
        margin: "0",
      },
      "& > .check-boxes label, span": {
        margin: "0 !important",
        columnGap: "10px",
      },
      "& > .date-picker > div": {
        margin: "0 !important",
        alignSelf: "right",
      },
    },
    // Next to the page title, laid out as one row of actions.
    reportActions: {
      display: "flex",
      alignItems: "flex-end",
      gap: "16px",
      flexShrink: 0,
      marginBottom: "12px",
    },
    downloadRow: {
      display: "flex",
      alignItems: "flex-end",
      gap: "10px",
      "& .MuiAutocomplete-root": {
        width: "200px",
      },
    },
    mobileFilters: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      rowGap: "10px",
      padding: "5px",
      "& > div": {
        margin: "0",
        width: "100%",
        minWidth: "0",
      },
    },
    downloadSection: {
      position: "absolute",
      width: "300px",
      top: "1rem",
      right: "1rem",
    },
    dialogFilters: {
      padding: "30px 15px",
      display: "flex",
      flexDirection: "column",
      rowGap: "15px",
      "& .date-picker": {
        flexDirection: "column !important",
        rowGap: "10px",
      },
      "& .date-picker > div": {
        width: "100% !important",
      },
      "& > .check-boxes": {
        paddingLeft: "1rem",
      },
    },
    tableCell: {
      display: "flex",
      maxHeight: "1.75rem",
      width: "fit-content",
      justifyContent: "center",
      alignItems: "center",
      borderRadius: "1.375rem",
      padding: "0.75rem",
      margin: "0 auto",
      "& .MuiButton-root": {
        minWidth: 0,
        paddingInline: 0,
      },
      "& .MuiSvgIcon-fontSizeMedium": {
        transform: "scale(0.8) !important",
      },
    },
    totalHours: {
      paddingLeft: "1.75rem",
      textAlign: "left",
      fontSize: "0.875rem !important",
    },
    [theme.breakpoints.down(1580)]: {
      filtersGroup: {
        "&> div:first-child": {
          width: "100px",
        },
        "& > .date-picker > div": {
          width: "145px !important",
        },
      },
      downloadSection: {
        width: "35%",
        top: "1rem",
        right: "1rem",
      },
    },
    [theme.breakpoints.down(800)]: {
      filtersGroup: {
        "& > div:first-child": {
          minWidth: "0",
          width: "230px ",
        },
        "& > .date-picker > div": {
          width: "110px !important",
        },
      },
      downloadSection: {
        width: "40%",
        top: "1rem",
        right: "1rem",
      },
    },
    [theme.breakpoints.down(MOBILE_WIDTH)]: {
      downloadSection: {
        // gridTemplateColumns: '1fr 1',
        // display: 'grid',
        position: "static",
        width: "100%",
      },
    },
    linkButton: {
      color: `${Token.textLink} !important`,
      textDecoration: "underline !important",
    },
  }),
  { index: 1 },
);
